"use client";

import { useState } from "react";

/*
 * The impact–effort matrix.
 *
 * ALWAYS 2x2, at every width. The design is explicit about this: the axis
 * labels are meaningless otherwise, so it never reflows to a single column the
 * way the lattices do.
 *
 * The quick-wins quadrant lists 1–2–3–… deliberately without a fixed count,
 * because the number of quick wins is whatever the hour produced.
 */
const QUADRANTS = [
  { key: "quick", label: "Quick wins", accent: true,
    body: "High impact, low effort. Itemised one by one, with the tool, the cost and the setup time. How many there are depends on what we find.",
    tail: "1 · 2 · 3 · …" },
  { key: "projects", label: "Major projects", accent: false,
    body: "High impact, high effort. Phased in after the wins have paid for them." },
  { key: "fill", label: "Fill-ins", accent: false,
    body: "Low impact, low effort. Worth doing when someone has a slow afternoon." },
  { key: "avoid", label: "Ignore these", accent: false,
    body: "Low impact, high effort. Named explicitly, so they stop coming back." },
];

export function Matrix() {
  const [hover, setHover] = useState<string | null>(null);

  return (
    <div style={{ display: "flex", gap: 10 }}>
      <div style={{
        writingMode: "vertical-rl", transform: "rotate(180deg)",
        fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: ".14em",
        textTransform: "uppercase", color: "var(--color-accent-700)",
        alignSelf: "center", paddingBottom: 26,
      }}>Impact →</div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr",
          aspectRatio: "1 / .82", gap: 2,
        }}>
          {QUADRANTS.map((q) => (
            <div key={q.key}
                 onMouseEnter={() => setHover(q.key)}
                 onMouseLeave={() => setHover(null)}
                 style={{
                   padding: "16px 14px",
                   background: q.accent
                     ? `color-mix(in srgb, var(--color-accent) ${hover === q.key ? 18 : 11}%, transparent)`
                     : hover === q.key
                       ? "color-mix(in srgb, var(--color-text) 5%, transparent)"
                       : "transparent",
                   boxShadow: "0 0 0 .5px var(--color-divider)",
                   transition: "background .35s",
                 }}>
              <div style={{ fontFamily: "var(--font-heading)", fontWeight: 600, fontSize: 16, marginBottom: 3 }}>
                {q.label}
              </div>
              <p className="card-body" style={{ fontSize: 12.5, lineHeight: 1.5, margin: 0 }}>{q.body}</p>
              {q.tail && (
                <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                  {["1", "2", "3"].map((n) => (
                    <span key={n} style={{
                      display: "grid", placeItems: "center", width: 22, height: 22,
                      border: "1px solid var(--color-accent)", color: "var(--color-accent-900)",
                      fontFamily: "var(--font-mono)", fontSize: 11,
                    }}>{n}</span>
                  ))}
                  {/* Dashed, because the count is deliberately open: however
                      many the hour produced. */}
                  <span style={{
                    display: "grid", placeItems: "center", width: 22, height: 22,
                    border: "1px dashed var(--color-accent)", color: "var(--color-accent-700)",
                    fontFamily: "var(--font-mono)", fontSize: 11,
                  }}>…</span>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="kicker" style={{
          display: "flex", justifyContent: "space-between", marginTop: 10,
        }}>
          <span>Low effort</span><span style={{ fontWeight: 700 }}>Effort</span><span>High effort</span>
        </div>
      </div>
    </div>
  );
}
